/**
 * Pre-parse LAB clustering for messy labelmaps.
 *
 * Reduces an arbitrary-colour labelmap to at most `maxK` colour regions
 * by running weighted k-means in CIE-LAB space on the coloured-pixel pool,
 * then optionally merging close centres by delta-E.
 *
 * Contract (A7):
 *   - Transparent (alpha < 128) and pure-black (R=G=B=0, opaque) pixels
 *     are passed through unchanged (label-0 pool).
 *   - All other pixels enter the clustering pool.
 *   - Output is a colour-reduced ImageData suitable for parseLabelmapImage.
 *
 * Pure portable module - no DOM globals beyond ImageData constructor.
 * Imports: srgbToLab, labToSrgb, deltaE from ./color-lab;
 *          fnv1a32 from ./content-hash.
 */

import { deltaE, labToSrgb, srgbToLab } from './color-lab';
import type { Lab, Rgb } from './color-lab';
import { fnv1a32 } from './content-hash';

// ─── Public types ─────────────────────────────────────────────────────────────

export type ClusteringParams = {
  maxK: number;    // user-set, clamped to 2..64
  deltaE: number;  // post-merge threshold; 0 means no post-merge
};

export type PreprocessResult = {
  output: ImageData;         // colour-reduced ImageData, fed to parseLabelmapImage
  rawUniqueColours: number;  // unique colours in input (excluding label-0 pool)
  resultingClusters: number; // cluster count after k-means + optional post-merge
};

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_K = 2;
const MAX_K = 64;
const MAX_ITERATIONS = 20;

// ─── Seeded PRNG (Mulberry32) ─────────────────────────────────────────────────

/**
 * Create a Mulberry32 PRNG seeded from a 32-bit unsigned integer.
 * Returns a function that produces floats in [0, 1) on each call.
 */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function (): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 15), z | 1);
    z ^= z + Math.imul(z ^ (z >>> 7), z | 61);
    return ((z ^ (z >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Histogram seed derivation ────────────────────────────────────────────────

/**
 * Derive a deterministic 32-bit seed from a weighted RGB histogram.
 *
 * Canonical serialisation: sort entries by packed-RGB ascending,
 * concatenate `${r}${g}${b}${count}` per entry, encode as UTF-8
 * bytes, feed to FNV-1a 32-bit.
 */
function histogramSeed(histogram: Map<number, number>): number {
  // Sort by packed-RGB ascending.
  const entries = Array.from(histogram.entries()).sort((a, b) => a[0]! - b[0]!);

  // Build the canonical byte string.
  let canonical = '';
  for (const [packed, count] of entries) {
    const r = (packed >>> 16) & 0xff;
    const g = (packed >>> 8) & 0xff;
    const b = packed & 0xff;
    canonical += `${r}${g}${b}${count}`;
  }

  // Encode as bytes and hash.
  const bytes = new TextEncoder().encode(canonical);
  const hexHash = fnv1a32(bytes);
  return parseInt(hexHash, 16) >>> 0;
}

// ─── k-means++ initialisation ─────────────────────────────────────────────────

/**
 * Select initial cluster centres using k-means++ weighted by pixel count,
 * with a seeded PRNG for reproducibility.
 *
 * @param colours     Unique colour entries [{ packed, lab, weight }]
 * @param k           Number of centres to select
 * @param rng         Seeded PRNG returning floats in [0, 1)
 */
function kmeansppInit(
  colours: { packed: number; lab: Lab; weight: number }[],
  k: number,
  rng: () => number,
): Lab[] {
  const n = colours.length;
  const centres: Lab[] = [];

  // First centre: pick weighted by pixel count.
  const totalWeight = colours.reduce((s, c) => s + c.weight, 0);
  let threshold = rng() * totalWeight;
  let firstIdx = 0;
  for (let i = 0; i < n; i++) {
    threshold -= colours[i]!.weight;
    if (threshold <= 0) {
      firstIdx = i;
      break;
    }
  }
  centres.push(colours[firstIdx]!.lab);

  // Subsequent centres: weighted by D² × pixel count.
  const dSq = new Float64Array(n);

  for (let ci = 1; ci < k; ci++) {
    // Compute squared distance from each colour to its nearest existing centre.
    let totalDSq = 0;
    for (let i = 0; i < n; i++) {
      let minDSq = Infinity;
      const lab = colours[i]!.lab;
      for (const centre of centres) {
        const dL = lab[0]! - centre[0]!;
        const da = lab[1]! - centre[1]!;
        const db = lab[2]! - centre[2]!;
        const d2 = dL * dL + da * da + db * db;
        if (d2 < minDSq) minDSq = d2;
      }
      const w = minDSq * colours[i]!.weight;
      dSq[i] = w;
      totalDSq += w;
    }

    if (totalDSq === 0) {
      // All remaining colours are exactly on an existing centre; stop early.
      break;
    }

    // Pick next centre weighted by D² × weight.
    let pick = rng() * totalDSq;
    let nextIdx = n - 1;
    for (let i = 0; i < n; i++) {
      pick -= dSq[i]!;
      if (pick <= 0) {
        nextIdx = i;
        break;
      }
    }
    centres.push(colours[nextIdx]!.lab);
  }

  return centres;
}

// ─── Weighted LAB k-means (Lloyd's algorithm) ─────────────────────────────────

/**
 * Run Lloyd's algorithm on the weighted colour set.
 * Returns the converged cluster assignment for each colour (index into centres).
 */
function kmeansLloyd(
  colours: { lab: Lab; weight: number }[],
  centres: Lab[],
): { centres: Lab[]; assignments: Uint16Array } {
  const n = colours.length;
  const k = centres.length;
  const assignments = new Uint16Array(n);

  // Mutable centre copy (Float64 arrays: [L, a, b]).
  let currentCentres: Lab[] = centres.map(c => [c[0]!, c[1]!, c[2]!]);

  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    // ── Assign each colour to its nearest centre ──
    let changed = false;
    for (let i = 0; i < n; i++) {
      const lab = colours[i]!.lab;
      let bestDist = Infinity;
      let bestCentre = 0;
      for (let ci = 0; ci < k; ci++) {
        const c = currentCentres[ci]!;
        const dL = lab[0]! - c[0]!;
        const da = lab[1]! - c[1]!;
        const db = lab[2]! - c[2]!;
        const dist = dL * dL + da * da + db * db;
        if (dist < bestDist) {
          bestDist = dist;
          bestCentre = ci;
        }
      }
      if (assignments[i] !== bestCentre) {
        assignments[i] = bestCentre;
        changed = true;
      }
    }

    if (!changed && iter > 0) break;

    // ── Recompute centres as weighted mean in LAB ──
    // Use Float64Array accumulators to avoid precision loss.
    const sumL = new Float64Array(k);
    const sumA = new Float64Array(k);
    const sumB = new Float64Array(k);
    const sumW = new Float64Array(k);

    for (let i = 0; i < n; i++) {
      const ci = assignments[i]!;
      const w = colours[i]!.weight;
      const lab = colours[i]!.lab;
      sumL[ci] = sumL[ci]! + lab[0]! * w;
      sumA[ci] = sumA[ci]! + lab[1]! * w;
      sumB[ci] = sumB[ci]! + lab[2]! * w;
      sumW[ci] = sumW[ci]! + w;
    }

    const newCentres: Lab[] = [];
    for (let ci = 0; ci < k; ci++) {
      const w = sumW[ci]!;
      if (w > 0) {
        newCentres.push([sumL[ci]! / w, sumA[ci]! / w, sumB[ci]! / w]);
      } else {
        // Empty cluster: retain previous centre.
        newCentres.push(currentCentres[ci]!);
      }
    }
    currentCentres = newCentres;
  }

  return { centres: currentCentres, assignments };
}

// ─── Post-merge by delta-E ────────────────────────────────────────────────────

/**
 * Iteratively merge pairs of cluster centres closer than `threshold` delta-E.
 * Each merge takes a weighted mean of the two centres (by total pixel count).
 * Repeats until no more merges are possible.
 *
 * Returns new centres and a remapping array: `mergeMap[oldCluster] = newCluster`.
 */
function postMerge(
  centres: Lab[],
  clusterWeights: Float64Array,
  threshold: number,
): { centres: Lab[]; mergeMap: number[] } {
  // Work on mutable copies.
  const current = centres.map(c => ({ lab: [c[0]!, c[1]!, c[2]!] as Lab, weight: clusterWeights[centres.indexOf(c)]! }));

  // Build initial mergeMap as identity.
  let mergeMap = current.map((_, i) => i);

  let merging = true;
  while (merging) {
    merging = false;
    for (let i = 0; i < current.length; i++) {
      for (let j = i + 1; j < current.length; j++) {
        if (deltaE(current[i]!.lab, current[j]!.lab) < threshold) {
          // Merge j into i (weighted mean).
          const wi = current[i]!.weight;
          const wj = current[j]!.weight;
          const wTotal = wi + wj;
          const merged: Lab = [
            (current[i]!.lab[0]! * wi + current[j]!.lab[0]! * wj) / wTotal,
            (current[i]!.lab[1]! * wi + current[j]!.lab[1]! * wj) / wTotal,
            (current[i]!.lab[2]! * wi + current[j]!.lab[2]! * wj) / wTotal,
          ];
          current[i] = { lab: merged, weight: wTotal };
          // Remove j.
          current.splice(j, 1);
          // Update mergeMap: any old index pointing to j now points to i.
          // Shift all indices > j down by 1.
          mergeMap = mergeMap.map(v => {
            if (v === j) return i;
            if (v > j) return v - 1;
            return v;
          });
          merging = true;
          // Restart inner loops.
          break;
        }
      }
      if (merging) break;
    }
  }

  return { centres: current.map(c => c.lab), mergeMap };
}

// ─── Public entry point ───────────────────────────────────────────────────────

/**
 * Preprocess a labelmap ImageData by clustering its colour palette in LAB space.
 *
 * Transparent and pure-black pixels are passed through unchanged.
 * Coloured pixels are remapped to the nearest cluster centre.
 */
export function preprocessLabelmap(
  source: ImageData,
  params: ClusteringParams,
): PreprocessResult {
  const k = Math.max(MIN_K, Math.min(MAX_K, params.maxK));
  const src = source.data;
  const total = source.width * source.height;

  // ── Step 1: Build weighted histogram over coloured pixels ──────────────────

  // histogram: packed RGB → pixel count
  const histogram = new Map<number, number>();

  for (let i = 0; i < total; i++) {
    const base = i * 4;
    const r = src[base]!;
    const g = src[base + 1]!;
    const b = src[base + 2]!;
    const a = src[base + 3]!;

    // Transparent pool: alpha < 128.
    if (a < 128) continue;
    // Pure-black pool: R=G=B=0, opaque.
    if (r === 0 && g === 0 && b === 0) continue;

    const packed = (r << 16) | (g << 8) | b;
    histogram.set(packed, (histogram.get(packed) ?? 0) + 1);
  }

  const rawUniqueColours = histogram.size;

  // Edge case: no coloured pixels → return source unchanged with 0 clusters.
  if (rawUniqueColours === 0) {
    const output = new ImageData(
      new Uint8ClampedArray(src),
      source.width,
      source.height,
    );
    return { output, rawUniqueColours: 0, resultingClusters: 0 };
  }

  // ── Step 2: Convert histogram to LAB colour entries ───────────────────────

  const colourEntries: { packed: number; lab: Lab; weight: number }[] = [];
  for (const [packed, weight] of histogram) {
    const r = (packed >>> 16) & 0xff;
    const g = (packed >>> 8) & 0xff;
    const b = packed & 0xff;
    colourEntries.push({ packed, lab: srgbToLab([r, g, b]), weight });
  }

  // If we have fewer unique colours than k, no clustering needed.
  const effectiveK = Math.min(k, colourEntries.length);

  // ── Step 3: k-means++ + Lloyd's algorithm ─────────────────────────────────

  // Derive a deterministic seed from the histogram.
  const seed = histogramSeed(histogram);
  const rng = mulberry32(seed);

  let centres = kmeansppInit(colourEntries, effectiveK, rng);
  const { centres: convergedCentres, assignments } = kmeansLloyd(colourEntries, centres);
  centres = convergedCentres;

  // ── Step 4: Optional post-merge by delta-E ─────────────────────────────────

  let finalCentres = centres;
  let finalAssignments: number[] = Array.from(assignments);

  if (params.deltaE > 0 && centres.length > 1) {
    // Compute per-cluster total weight for weighted merge.
    const clusterWeights = new Float64Array(centres.length);
    for (let i = 0; i < colourEntries.length; i++) {
      const ci = assignments[i]!;
      clusterWeights[ci] = clusterWeights[ci]! + colourEntries[i]!.weight;
    }

    const { centres: mergedCentres, mergeMap } = postMerge(centres, clusterWeights, params.deltaE);
    finalCentres = mergedCentres;
    // Remap assignments through the merge map.
    finalAssignments = finalAssignments.map(ci => mergeMap[ci]!);
  }

  const resultingClusters = finalCentres.length;

  // ── Step 5: Build packed-RGB → cluster centre RGB lookup table ────────────

  // Convert each final centre from LAB back to sRGB.
  const centreRgb: Rgb[] = finalCentres.map(lab => labToSrgb(lab));

  // Map: packed input RGB → [r, g, b] output RGB.
  const lookup = new Map<number, Rgb>();
  for (let i = 0; i < colourEntries.length; i++) {
    const packed = colourEntries[i]!.packed;
    const ci = finalAssignments[i]!;
    lookup.set(packed, centreRgb[ci]!);
  }

  // ── Step 6: Pixel rewrite ─────────────────────────────────────────────────

  const outData = new Uint8ClampedArray(total * 4);

  for (let i = 0; i < total; i++) {
    const base = i * 4;
    const r = src[base]!;
    const g = src[base + 1]!;
    const b = src[base + 2]!;
    const a = src[base + 3]!;

    // Transparent pool: pass through as-is.
    if (a < 128) {
      outData[base] = r;
      outData[base + 1] = g;
      outData[base + 2] = b;
      outData[base + 3] = a;
      continue;
    }

    // Pure-black pool: pass through as-is.
    if (r === 0 && g === 0 && b === 0) {
      outData[base] = 0;
      outData[base + 1] = 0;
      outData[base + 2] = 0;
      outData[base + 3] = a;
      continue;
    }

    // Coloured pool: remap to cluster centre.
    const packed = (r << 16) | (g << 8) | b;
    const mapped = lookup.get(packed);
    if (mapped !== undefined) {
      outData[base] = mapped[0]!;
      outData[base + 1] = mapped[1]!;
      outData[base + 2] = mapped[2]!;
      outData[base + 3] = 255;
    } else {
      // Fallback: copy as-is (should not occur given histogram coverage).
      outData[base] = r;
      outData[base + 1] = g;
      outData[base + 2] = b;
      outData[base + 3] = a;
    }
  }

  const output = new ImageData(outData, source.width, source.height);
  return { output, rawUniqueColours, resultingClusters };
}
